import { COLORS, baseStyles } from "../shared/constants";
import { useCallback, useEffect, useState } from "react";
import * as api from "../api";

// ─── Device Pairing Screen ───────────────────────────────────
//
// First-time setup for an unpaired iPad (or one whose Safari data was
// cleared, wiping localStorage). Operator types the 6-digit code that
// an admin generated via Settings → Devices on a paired device.
//
// On success, api.devicePairComplete persists device_id + device_token
// in localStorage and we call onPaired() so the parent (LoginScreen) can
// re-fetch the operator list and proceed to the picker.

export default function DevicePairing({ onPaired, onCancel }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (fullCode) => {
    setSubmitting(true);
    setError("");
    try {
      const data = await api.devicePairComplete(fullCode);
      // api.devicePairComplete already persists creds to localStorage.
      // Tiny pause for the success state to register, then bubble up.
      setTimeout(() => onPaired && onPaired(data), 200);
    } catch (e) {
      setError(
        e.detail ||
          e.error ||
          "Pair code not recognized. Check with your administrator."
      );
      setTimeout(() => setCode(""), 300);
      setSubmitting(false);
    }
  };

  const handleDigit = (digit) => {
    if (submitting) return;
    if (code.length >= 6) return;
    const newCode = code + digit;
    setCode(newCode);
    setError("");
    if (newCode.length === 6) submit(newCode);
  };

  const handleBackspace = () => {
    setCode((c) => c.slice(0, -1));
    setError("");
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key >= "0" && e.key <= "9") handleDigit(e.key);
      else if (e.key === "Backspace") handleBackspace();
    },
    [code, submitting],
  );
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div style={{
      ...baseStyles,
      background: `linear-gradient(145deg, ${COLORS.primary} 0%, #0F3D52 100%)`,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
    }}>
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <h1 style={{ color: "#FFFFFF", fontSize: "28px", fontWeight: "700", letterSpacing: "0.5px", margin: 0 }}>
          Pair this iPad
        </h1>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", marginTop: "8px", letterSpacing: "1px", textTransform: "uppercase" }}>
          First-time setup
        </p>
      </div>

      <div style={{
        background: COLORS.surface,
        borderRadius: "16px",
        padding: "40px 36px",
        width: "100%",
        maxWidth: "360px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        position: "relative",
      }}>
        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              position: "absolute",
              top: "16px",
              left: "16px",
              background: "transparent",
              border: "none",
              fontSize: "20px",
              color: COLORS.textSecondary,
              cursor: "pointer",
              padding: "4px 8px",
            }}
            aria-label="Cancel pairing"
          >
            ‹ Back
          </button>
        )}

        <p style={{
          textAlign: "center",
          color: COLORS.textSecondary,
          fontSize: "14px",
          marginBottom: "24px",
          fontWeight: "500",
        }}>
          Enter the 6-digit pair code your administrator generated
        </p>

        {/* Code dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginBottom: "28px" }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              background: i < code.length ? COLORS.primary : "transparent",
              border: `2px solid ${i < code.length ? COLORS.primary : COLORS.border}`,
              transition: "all 0.15s ease",
              transform: i < code.length ? "scale(1.1)" : "scale(1)",
            }} />
          ))}
        </div>

        {error && (
          <p style={{
            textAlign: "center",
            color: COLORS.danger,
            fontSize: "13px",
            marginBottom: "16px",
            fontWeight: "500",
          }}>
            {error}
          </p>
        )}

        {/* Number pad */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, "⌫"].map((key, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (key === "⌫") handleBackspace();
                else if (key !== null) handleDigit(String(key));
              }}
              disabled={submitting || key === null}
              style={{
                height: "60px",
                borderRadius: "12px",
                border: "none",
                background: key === null ? "transparent" : COLORS.inputBg,
                color: COLORS.text,
                fontSize: key === "⌫" ? "20px" : "22px",
                fontWeight: "600",
                cursor: key === null || submitting ? "default" : "pointer",
                opacity: key === null ? 0 : submitting ? 0.4 : 1,
                transition: "background 0.15s",
              }}
              onMouseDown={(e) => {
                if (key !== null && !submitting) e.currentTarget.style.background = COLORS.border;
              }}
              onMouseUp={(e) => {
                if (key !== null && !submitting) e.currentTarget.style.background = COLORS.inputBg;
              }}
              onMouseLeave={(e) => {
                if (key !== null && !submitting) e.currentTarget.style.background = COLORS.inputBg;
              }}
            >
              {key}
            </button>
          ))}
        </div>

        <p style={{
          textAlign: "center",
          color: COLORS.textSecondary,
          fontSize: "11px",
          marginTop: "24px",
        }}>
          Codes expire 15 minutes after generation. Ask your admin to generate a new one if needed.
        </p>
      </div>
    </div>
  );
}
